/**
 * nodesOnAnyPath - returns the set of node keys that lie on any path from source to target.
 * O(V+E) time using forward + backward DFS with bitset intersection.
 * Excludes the target node itself (matching web-app getAvailableSteps behavior).
 */
import type Graph from './index';

function resolveIdx(graph: Graph, key: string, role: string): number {
    const idx = graph._nodeIndex.get(key);
    if (idx === undefined) {
        throw new Error(
            `graphology-simple-path.allSimplePaths: expecting: could not find ${role} node "${key}" in the graph.`
        );
    }
    return idx;
}

function dfsReachable(startIdx: number, adjList: number[][], words: number): Uint32Array {
    const bitset = new Uint32Array(words);
    const stack = [ startIdx ];
    bitset[startIdx >> 5] |= (1 << (startIdx & 31));

    while (stack.length > 0) {
        const node = stack.pop()!;
        for (const next of adjList[node]) {
            if ((bitset[next >> 5] & (1 << (next & 31))) === 0) {
                bitset[next >> 5] |= (1 << (next & 31));
                stack.push(next);
            }
        }
    }
    return bitset;
}

export function nodesOnAnyPath(graph: Graph, source: string, target: string): Set<string> {
    const sourceIdx = resolveIdx(graph, source, 'source');
    const targetIdx = resolveIdx(graph, target, 'target');

    const n = graph._size;
    const words = Math.ceil(n / 32) || 1;

    const forwardSet = dfsReachable(sourceIdx, graph._adj, words);
    const backwardSet = dfsReachable(targetIdx, graph._radj, words);

    // Intersection: nodes reachable from source AND that can reach target, excluding target
    const result = new Set<string>();
    const nodeKeys = graph._nodeKeys;
    for (let w = 0; w < words; w++) {
        let bits = forwardSet[w] & backwardSet[w];
        while (bits !== 0) {
            const bit = bits & (-bits); // lowest set bit
            const idx = (w << 5) + (31 - Math.clz32(bit));
            if (idx !== targetIdx && idx < n) {
                result.add(nodeKeys[idx]);
            }
            bits ^= bit;
        }
    }

    return result;
}
